import { create } from "zustand";
import { devtools } from "zustand/middleware";
import * as FavoriteProductApi from "@src/api/FavoriteProductApi";
import { PaginationValues } from "@src/types/pagination";
import { FavoriteProduct } from "@src/types/favoriteProduct";

const DEFAULT_PAGINATION: PaginationValues = {
  pageSize: 20,
  page: 1,
  totalPages: 0,
  totalCount: 0,
};

type FavoriteProductsState = {
  favoriteIds: Set<string>;
  favorites: FavoriteProduct[] | null;
  loading: boolean;
  error: string | null;
  pagination: PaginationValues;
  fetchFavoriteIds: () => Promise<void>;
  fetchFavorites: (paginationOverrides?: Partial<PaginationValues>) => Promise<void>;
  addFavorite: (productId: string) => Promise<void>;
  removeFavorite: (productId: string) => Promise<void>;
  isFavorited: (productId: string) => boolean;
  setPagination: (pagination: PaginationValues) => void;
};

export const useFavoriteProductsStore = create<FavoriteProductsState>()(
  devtools((set, get) => {
    const store: FavoriteProductsState = {
      favoriteIds: new Set(),
      favorites: null,
      loading: false,
      error: null,
      pagination: DEFAULT_PAGINATION,

      fetchFavoriteIds: async () => {
        try {
          const response = await FavoriteProductApi.getFavoriteProductIds();
          const ids: string[] = response.body?.ids ?? [];
          set(
            { favoriteIds: new Set(ids) },
            false,
            "favorites/fetch-ids",
          );
        } catch (err: any) {
          console.error("Failed to fetch favorite product IDs:", err);
        }
      },

      fetchFavorites: async (paginationOverrides = {}) => {
        set({ loading: true, error: null }, false, "favorites/fetch-start");

        try {
          const currentPagination = get().pagination;
          const nextPagination = { ...currentPagination, ...paginationOverrides };
          const response = await FavoriteProductApi.getFavoriteProducts(nextPagination);
          const body = response.body;
          const favorites = Array.isArray(body) ? body : (body?.results ?? []);
          const totalCount =
            body?.totalCount ?? body?.total_count ?? body?.count ?? favorites.length;
          const totalPages = body?.totalPages ?? body?.total_pages ?? 1;
          const page = body?.page ?? nextPagination.page;

          const favoriteIds = new Set<string>();
          for (const fav of favorites) {
            if (fav.product?.id) {
              favoriteIds.add(String(fav.product.id));
            }
          }

          set(
            {
              favorites,
              favoriteIds,
              loading: false,
              pagination: { ...nextPagination, page, totalCount, totalPages },
            },
            false,
            "favorites/fetch-success",
          );
        } catch (err: any) {
          set(
            { error: err.message || "Unknown error while fetching favorites", loading: false },
            false,
            "favorites/fetch-error",
          );
        }
      },

      addFavorite: async (productId: string) => {
        const prevIds = get().favoriteIds;
        set(
          { favoriteIds: new Set([...prevIds, productId]) },
          false,
          "favorites/add-optimistic",
        );

        try {
          await FavoriteProductApi.favoriteProduct(productId);
        } catch (err: any) {
          console.error("Failed to add favorite:", err);
          set(
            (state) => {
              const next = new Set(state.favoriteIds);
              next.delete(productId);
              return { favoriteIds: next };
            },
            false,
            "favorites/add-rollback",
          );
        }
      },

      removeFavorite: async (productId: string) => {
        const prevIds = get().favoriteIds;
        const nextIds = new Set(prevIds);
        nextIds.delete(productId);

        set(
          { favoriteIds: nextIds },
          false,
          "favorites/remove-optimistic",
        );

        try {
          await FavoriteProductApi.unfavoriteProduct(productId);
        } catch (err: any) {
          console.error("Failed to remove favorite:", err);
          set(
            (state) => ({
              favoriteIds: new Set([...state.favoriteIds, productId]),
            }),
            false,
            "favorites/remove-rollback",
          );
        }
      },

      isFavorited: (productId: string) => {
        return get().favoriteIds.has(productId);
      },

      setPagination: (pagination: PaginationValues) => {
        set({ pagination }, false, "favorites/set-pagination");
      },
    };

    return store;
  }),
);
